import { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Check, Maximize2, Search, Loader2 } from 'lucide-react'
import { RootState } from '@/store/store'
import { toggleImageSelection, openImageModal } from '@/store/slices/uiSlice'
import { startImageSearch, setSearchResults, setSearchError } from '@/store/slices/searchSlice'
import { useLazySearchByImageQuery } from '@/store/api'
import type { ImageDocument } from '@/store/api'
import { previewUrl } from '@/utils/media'
import { useSignedUrls } from '@/utils/useSignedUrls'

interface ImageGridProps {
  images: ImageDocument[]
  total: number
  onLoadMore?: () => void
  onLoadAll?: () => void
  hasMore?: boolean
  isLoadingAll?: boolean
}

function ImageGrid({ images, total, onLoadMore, onLoadAll, hasMore, isLoadingAll }: ImageGridProps) {
  useSignedUrls()
  const dispatch = useDispatch()
  const selectedImages = useSelector((state: RootState) => state.ui.selectedImages)
  const gridSize = useSelector((state: RootState) => state.ui.gridSize)
  const [searchingId, setSearchingId] = useState<string | null>(null)

  // Grid column classes based on size
  const gridColsClass = gridSize === 'small'
    ? 'grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10'
    : gridSize === 'medium'
    ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6'
    : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4'

  const [triggerSearchByImage] = useLazySearchByImageQuery()

  // Build list of image IDs for modal navigation
  const imageIds = images.map(img => img.master_id)

  const handleImageClick = useCallback((masterId: string, e: React.MouseEvent) => {
    e.preventDefault()
    // Regular click toggles selection
    dispatch(toggleImageSelection(masterId))
  }, [dispatch])

  const handleExpandClick = useCallback((masterId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    // Open modal with full image list for navigation
    dispatch(openImageModal({ masterId, imageList: imageIds }))
  }, [dispatch, imageIds])

  const handleCheckboxClick = useCallback((masterId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    dispatch(toggleImageSelection(masterId))
  }, [dispatch])

  const handleFindSimilar = useCallback(async (masterId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (searchingId) return

    setSearchingId(masterId)
    dispatch(startImageSearch(masterId))

    try {
      const results = await triggerSearchByImage({ masterId, limit: 20 }).unwrap()
      dispatch(setSearchResults(results))
    } catch (error) {
      dispatch(setSearchError(error instanceof Error ? error.message : 'Search failed'))
    } finally {
      setSearchingId(null)
    }
  }, [dispatch, triggerSearchByImage, searchingId])

  if (images.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-muted-foreground">
          <p className="text-lg mb-2">No images found</p>
          <p className="text-sm">Try adjusting your filters or process some images first</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="text-sm text-muted-foreground mb-4">
        Showing {images.length} of {total} images
      </div>

      <div className={`grid ${gridColsClass} gap-2`}>
        {images.map((image) => {
          const isSelected = selectedImages.includes(image.master_id)

          return (
            <div
              key={image.master_id}
              onClick={(e) => handleImageClick(image.master_id, e)}
              className={`
                relative aspect-square bg-secondary rounded-lg overflow-hidden cursor-pointer
                transition-all group
                ${isSelected ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : 'hover:ring-1 hover:ring-border'}
              `}
            >
              {/* Image */}
              <img
                src={previewUrl(image.master_id)}
                alt={image.caption || image.file_name}
                loading="lazy"
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/placeholder.svg'
                }}
              />

              {/* Selection Checkbox - top left */}
              <div
                onClick={(e) => handleCheckboxClick(image.master_id, e)}
                className={`
                  absolute top-2 left-2 w-6 h-6 rounded-full border-2 flex items-center justify-center
                  transition-opacity cursor-pointer
                  ${isSelected
                    ? 'bg-primary border-primary opacity-100'
                    : 'bg-black/50 border-white/50 opacity-0 group-hover:opacity-100'
                  }
                `}
              >
                {isSelected && <Check size={14} className="text-primary-foreground" />}
              </div>

              {/* Action buttons - top right, shows on hover */}
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {/* Find Similar button */}
                <button
                  onClick={(e) => handleFindSimilar(image.master_id, e)}
                  disabled={searchingId === image.master_id}
                  className="w-8 h-8 rounded-lg bg-primary/80 hover:bg-primary
                             flex items-center justify-center cursor-pointer
                             disabled:opacity-50"
                  aria-label="Find similar images"
                  title="Find similar"
                >
                  {searchingId === image.master_id ? (
                    <Loader2 size={14} className="text-white animate-spin" />
                  ) : (
                    <Search size={14} className="text-white" />
                  )}
                </button>

                {/* Expand button */}
                <button
                  onClick={(e) => handleExpandClick(image.master_id, e)}
                  className="w-8 h-8 rounded-lg bg-black/60 hover:bg-black/80
                             flex items-center justify-center cursor-pointer"
                  aria-label="View full size"
                  title="View full size"
                >
                  <Maximize2 size={16} className="text-white" />
                </button>
              </div>

              {/* Classification Badge - below expand button */}
              {!image.classification?.is_photo && (
                <div className="absolute top-12 right-2 px-2 py-0.5 bg-black/70 rounded text-xs">
                  {image.classification?.is_screenshot ? 'Screenshot' : 'Document'}
                </div>
              )}

              {/* Face Indicator */}
              {image.face_ids?.length > 0 && (
                <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/70 rounded text-xs">
                  {image.face_ids.length} face{image.face_ids.length > 1 ? 's' : ''}
                </div>
              )}

              {/* Hover Overlay with Info */}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                <p className="text-xs text-white truncate">
                  {image.caption || image.file_name}
                </p>
                {image.tags?.length > 0 && (
                  <p className="text-xs text-white/70 truncate mt-0.5">
                    {image.tags.slice(0, 3).join(', ')}
                  </p>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Load More / Load All */}
      {(hasMore ?? images.length < total) && onLoadMore && (
        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            onClick={onLoadMore}
            disabled={isLoadingAll}
            className="px-4 py-2 bg-secondary hover:bg-secondary/80 rounded-md transition-colors disabled:opacity-50"
          >
            Load More ({images.length} of {total})
          </button>
          {onLoadAll && (
            <button
              onClick={onLoadAll}
              disabled={isLoadingAll}
              className="px-4 py-2 bg-primary/80 hover:bg-primary text-primary-foreground rounded-md transition-colors disabled:opacity-50"
            >
              {isLoadingAll ? `Loading... (${images.length} of ${total})` : 'Load All'}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default ImageGrid
